import {FormRow} from '@dropbox/dig-components/form_row';
import {TextInput, TextInputSize} from '@dropbox/dig-components/text_fields';
import {Typeahead, WrapperRef} from '@dropbox/dig-components/typeahead';
import {Text} from '@dropbox/dig-components/typography';
import {atoms} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {AddLine, SearchLine, TeamLine} from '@dropbox/dig-icons/assets';
import {TeamWithCounts, TeamWithCountsNew} from 'client';
import {useGlobalSearch} from 'hooks/useEmployee';
import {t} from 'i18next';
import {ComponentProps, Ref, useEffect, useRef, useState} from 'react';
import {Trans} from 'react-i18next';

import {AvatarIcon} from './Avatar';

const teamTypeaheadTextStyle: React.CSSProperties = {
  wordBreak: 'normal',
  overflowWrap: 'break-word',
  hyphens: 'manual',
};

const TypeaheadWrapper = Typeahead.Wrapper as (
  props: ComponentProps<typeof Typeahead.Wrapper> & {ref?: Ref<WrapperRef>}
  // eslint-disable-next-line deprecation/deprecation -- Existing issue; this suppression was only added to allow existing lint warnings to be upgraded to errors.
) => JSX.Element;

const dedupeTeams = (teams: TeamWithCountsNew[]) =>
  Array.from(
    new Map(
      teams.map((team) => {
        const key = team.team_id ?? team.slug ?? team.name ?? JSON.stringify(team);
        return [key, team];
      })
    ).values()
  );

export const TeamTypeahead = ({
  autoFocus,
  selection,
  selections,
  placeholder,
  onSingleSelect,
  onMultiSelect,
  inputClassName,
  isTransparent = false,
  textInputSize = 'large',
  wrapInFormRow = true,
  isPortaled = false,
  resultsWidth,
  resultsContainerStyle,
}: {
  autoFocus?: boolean;
  selection?: string;
  selections?: string[];
  placeholder: TeamWithCountsNew[];
  onSingleSelect?: (team: TeamWithCountsNew) => void;
  onMultiSelect?: (teams: TeamWithCountsNew[]) => void;
  inputClassName?: string;
  isTransparent?: boolean;
  textInputSize?: TextInputSize;
  wrapInFormRow?: boolean;
  isPortaled?: boolean;
  resultsWidth?: number | string;
  resultsContainerStyle?: React.CSSProperties;
}) => {
  if ((selection && !onSingleSelect) || (selections && !onMultiSelect)) {
    throw new Error(
      'Selections must be used with onMultiSelect and selection must be used with onSingleSelect'
    );
  }

  const [userInputValue, setUserInputValue] = useState('');
  const typeaheadWrapperRef = useRef<WrapperRef | null>(null);
  // const searchResult = useSearches({
  //   ldaps: userInputValue.split(',').map((e) => (!e.includes('@') ? e : e.trim().split('@')[0])),
  // });
  const {data: searchResult, isLoading} = useGlobalSearch({
    input: userInputValue,
    filter: 'teams',
    showMore: true,
  });

  const searchedResults = dedupeTeams(searchResult.map((result) => result as TeamWithCountsNew));

  const teamHints = searchedResults.length ? searchedResults : placeholder;

  const filteredTeamHints = teamHints.filter(
    (team) => !selections?.find((slug) => slug === team.slug) && team.slug !== selection
  );
  const shouldRenderResults =
    Boolean(userInputValue.length) || isLoading || filteredTeamHints.length > 0;

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUserInputValue(event.currentTarget.value);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if ((event.key === 'a' || event.key === 'A') && (event.metaKey || event.ctrlKey)) {
      event.stopPropagation();
    }
  };

  const handleSelection = (slug: string) => {
    const selectedTeam = teamHints.find((e) => e.slug === slug);
    if (selectedTeam) {
      onMultiSelect?.([selectedTeam]);
      onSingleSelect?.(selectedTeam);
      setUserInputValue('');
      requestAnimationFrame(() => {
        typeaheadWrapperRef.current?.focusTrigger();
      });
    }
  };

  useEffect(() => {
    if (userInputValue.length > 0 && userInputValue.includes(',') && searchResult.length) {
      let updatedInputValue = userInputValue;
      const foundTeams = userInputValue.split(',').reduce((acc, value) => {
        const normalizedValue = value.trim().toLowerCase();
        const found = teamHints.find(
          (e) => (e.name ?? '').toLowerCase() === normalizedValue || e.slug === normalizedValue
        );
        if (found) {
          updatedInputValue = updatedInputValue.replace(value, '');
          acc.push(found);
        }
        return acc;
      }, [] as TeamWithCountsNew[]);

      const newSelections = foundTeams.filter(
        (found) => !selections?.find((slug) => slug === found.slug)
      );

      if (newSelections.length) {
        onMultiSelect?.(newSelections);
      }

      setUserInputValue(
        updatedInputValue
          .split(',')
          .filter((e) => e.length)
          .join(',')
      );
    }
  }, [teamHints, onMultiSelect, searchResult, selections, userInputValue]);

  const renderTypeaheadRow = (team: TeamWithCountsNew) => (
    <Typeahead.Row
      key={team.team_id}
      value={team.slug}
      withTitle={<span style={teamTypeaheadTextStyle}>{team.name}</span>}
      withSubtitle={
        <span style={teamTypeaheadTextStyle}>
          {t('member', {
            count: (team as TeamWithCounts).members_count ?? 0,
            countString: (team as TeamWithCounts).members_count ?? 0,
          }) +
            ((team as TeamWithCounts).subteams_count
              ? ` • ${t('sub_team', {
                  count: (team as TeamWithCounts).subteams_count ?? 0,
                  countString: (team as TeamWithCounts).subteams_count ?? 0,
                })}`
              : '')}
        </span>
      }
      withLeftAccessory={<AvatarIcon src={TeamLine} />}
    />
  );

  return (
    <TypeaheadWrapper
      ref={typeaheadWrapperRef}
      onSelection={handleSelection}
      isPortaled={isPortaled}
    >
      {({getTriggerProps, getContentProps}) => {
        const input = (
          <TextInput
            autoFocus={autoFocus}
            name="team-typeahead"
            size={textInputSize}
            isTransparent={isTransparent}
            placeholder={t('select_teams')}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            value={userInputValue}
            withLeftAccessory={
              <UIIcon src={SearchLine} className={atoms({color: 'Text Subtle'})} />
            }
            style={{width: '100%'}}
            {...getTriggerProps({onChange: onInputChange, onKeyDown})}
          />
        );

        return (
          <div className={inputClassName} style={{width: '100%'}}>
            {wrapInFormRow ? <FormRow>{input}</FormRow> : input}

            {!shouldRenderResults ? null : (
              <Typeahead.Container
                {...getContentProps()}
                style={
                  resultsWidth
                    ? {width: resultsWidth, ...resultsContainerStyle}
                    : resultsContainerStyle
                }
              >
                {!userInputValue.length || isLoading || filteredTeamHints?.length ? (
                  <Typeahead.Results
                    maxResults={8}
                    results={filteredTeamHints}
                    renderRow={renderTypeaheadRow}
                  />
                ) : (
                  <Typeahead.Results
                    results={[{}]}
                    renderRow={() => (
                      <Typeahead.Row
                        withLeftAccessory={<AvatarIcon src={AddLine} />}
                        withTitle={
                          <Trans
                            i18nKey="team_not_found_create"
                            t={t}
                            values={{name: userInputValue}}
                            components={{b: <Text isBold />}}
                          />
                        }
                        onClick={() => {
                          window.open(`/teams/new?name=${userInputValue}`, '_blank');
                        }}
                      />
                    )}
                  />
                )}
              </Typeahead.Container>
            )}
          </div>
        );
      }}
    </TypeaheadWrapper>
  );
};
